import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import CloseIcon from '@material-ui/icons/Close';
import FolderIcon from '@material-ui/icons/Folder';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import VideoLibraryIcon from '@material-ui/icons/VideoLibrary';
import ImageIcon from '@material-ui/icons/Image';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import HomeIcon from '@material-ui/icons/Home';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import CircularProgress from '@material-ui/core/CircularProgress';
import styles from './DropboxFileBrowser.module.css';

const getFileIcon = (fileName, isFolder) => {
  if (isFolder) {
    return <FolderIcon />;
  }

  const extension = fileName.split('.').pop().toLowerCase();

  if (['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(extension)) {
    return <VideoLibraryIcon />;
  }
  // if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension)) {
  //   return <ImageIcon />;
  // }
  return <InsertDriveFileIcon />;
};

const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '';

  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${Math.round((bytes / (1024 ** i)) * 100) / 100} ${sizes[i]}`;
};

export const DropboxFileBrowser = ({ accessToken, onClose, onFilesSelected, onShowMessage, onDisconnect, onTokenExpired }) => {
  const [currentPath, setCurrentPath] = useState('');
  const [pathHistory, setPathHistory] = useState(['']);
  const [items, setItems] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedFolders, setSelectedFolders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingFolderId, setLoadingFolderId] = useState(null);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Filter items based on search query
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) {
      return items;
    }
    const query = searchQuery.toLowerCase().trim();
    return items.filter((item) => item.name.toLowerCase().includes(query));
  }, [items, searchQuery]);

  // Function to recursively get all video files from a folder
  const getAllVideosFromFolder = async (folderPath) => {
    try {
      const response = await axios.post(
        'https://api.dropboxapi.com/2/files/list_folder',
        {
          path: folderPath,
          recursive: true, // This will get all files from subfolders too
          include_deleted: false,
          include_mounted_folders: true,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        },
      );

      const allItems = response.data.entries || [];
      const videoExtensions = ['mp4', 'mov', 'avi', 'mkv', 'webm', 'flv', 'wmv', 'm4v', 'mpeg', 'mpg'];

      // Filter to only get video files
      const videoFiles = allItems.filter((item) => {
        if (item['.tag'] === 'file') {
          const fileName = item.name.toLowerCase();
          const extension = fileName.split('.').pop();
          return videoExtensions.includes(extension);
        }
        return false;
      });

      return videoFiles.map((file) => ({
        id: file.id,
        name: file.name,
        size: file.size,
        path: file.path_display,
      }));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error loading videos from folder:', err);
      return [];
    }
  };

  const loadFolder = async (path) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        'https://api.dropboxapi.com/2/files/list_folder',
        {
          path: path || '',
          recursive: false,
          include_deleted: false,
          include_mounted_folders: true,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        },
      );

      // Filter to show only folders, videos, and photos
      const allItems = response.data.entries || [];
      const filteredItems = allItems.filter((item) => {
        // Always show folders
        if (item['.tag'] === 'folder') {
          return true;
        }

        // For files, check if they're videos or photos
        if (item['.tag'] === 'file') {
          const fileName = item.name.toLowerCase();
          const videoExtensions = ['mp4', 'mov', 'avi', 'mkv', 'webm', 'flv', 'wmv', 'm4v', 'mpeg', 'mpg'];
          // const photoExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'heic', 'heif', 'tiff', 'tif'];
          const photoExtensions = [];

          const extension = fileName.split('.').pop();
          return videoExtensions.includes(extension) || photoExtensions.includes(extension);
        }

        return false;
      });

      setItems(filteredItems);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error loading Dropbox folder:', err);

      // Check if token expired (401 Unauthorized)
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        if (onTokenExpired) {
          onTokenExpired();
          return;
        }
      }

      setError('Failed to load folder contents');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (accessToken) {
      loadFolder(currentPath);
    }
    // eslint-disable-next-line
  }, [currentPath, accessToken]);

  const handleFolderClick = (folderPath) => {
    setPathHistory([...pathHistory, folderPath]);
    setCurrentPath(folderPath);
    setSearchQuery(''); // Clear search when navigating
  };

  const handleBackClick = () => {
    if (pathHistory.length > 1) {
      const newHistory = [...pathHistory];
      newHistory.pop();
      const previousPath = newHistory[newHistory.length - 1];
      setPathHistory(newHistory);
      setCurrentPath(previousPath);
      setSearchQuery(''); // Clear search when navigating back
    }
  };

  const handleHomeClick = () => {
    setPathHistory(['']);
    setCurrentPath('');
    setSearchQuery(''); // Clear search when going home
  };

  const handleFileToggle = (file) => {
    const fileId = file.id;
    const isSelected = selectedFiles.some((f) => f.id === fileId);

    if (isSelected) {
      setSelectedFiles(selectedFiles.filter((f) => f.id !== fileId));
    } else {
      setSelectedFiles([...selectedFiles, {
        id: fileId,
        name: file.name,
        size: file.size,
        path: file.path_display,
      }]);
    }
  };

  const handleFolderToggle = async (folder) => {
    const folderId = folder.id;
    const isSelected = selectedFolders.some((f) => f.id === folderId);

    if (isSelected) {
      // Deselect folder and remove all its files
      setSelectedFolders(selectedFolders.filter((f) => f.id !== folderId));
      const folderData = selectedFolders.find((f) => f.id === folderId);
      if (folderData && folderData.files) {
        // Remove all files that belong to this folder
        const folderFileIds = folderData.files.map((file) => file.id);
        setSelectedFiles(selectedFiles.filter((f) => !folderFileIds.includes(f.id)));
      }
    } else {
      // Select folder and get all its video files
      setLoadingFolderId(folderId);
      const videoFiles = await getAllVideosFromFolder(folder.path_display);
      setLoadingFolderId(null);

      if (videoFiles.length === 0) {
        // Show toast if no videos found
        if (onShowMessage) {
          onShowMessage('Nothing to select');
        }
      } else {
        // Mark each file with the folder name for category tracking
        const filesWithFolderName = videoFiles.map((file) => ({
          ...file,
          folder_name: folder.name,
        }));

        // Add folder to selected folders
        setSelectedFolders([...selectedFolders, {
          id: folderId,
          name: folder.name,
          path: folder.path_display,
          files: filesWithFolderName,
        }]);

        // Add all video files to selected files (avoid duplicates)
        const existingFileIds = selectedFiles.map((f) => f.id);
        const newFiles = filesWithFolderName.filter((file) => !existingFileIds.includes(file.id));
        setSelectedFiles([...selectedFiles, ...newFiles]);
      }
    }
  };

  const handleImport = () => {
    if (selectedFiles.length > 0) {
      onFilesSelected(selectedFiles);
    }
  };

  const isFileSelected = (fileId) => selectedFiles.some((f) => f.id === fileId);
  const isFolderSelected = (folderId) => selectedFolders.some((f) => f.id === folderId);

  const handleClearSelection = () => {
    setSelectedFiles([]);
    setSelectedFolders([]);
  };

  const handleSelectAll = () => {
    // Select all files in current folder
    const allFiles = items.filter((item) => item['.tag'] === 'file').map((file) => ({
      id: file.id,
      name: file.name,
      size: file.size,
      path: file.path_display,
    }));
    setSelectedFiles(allFiles);
  };

  const getCurrentFolderName = () => {
    if (!currentPath || currentPath === '') {
      return 'Dropbox';
    }
    return currentPath.split('/').pop();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div
      className={styles.overlay}
      onClick={handleOverlayClick}
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <div className={styles.breadcrumb}>
              <button
                className={styles.breadcrumbButton}
                onClick={handleHomeClick}
                disabled={currentPath === ''}
              >
                <HomeIcon style={{ fontSize: 16 }} />
              </button>
              {currentPath && (
                <>
                  <span className={styles.breadcrumbSeparator}>/</span>
                  <span className={styles.breadcrumbCurrent}>{getCurrentFolderName()}</span>
                </>
              )}
            </div>
            {!currentPath && <h2 className={styles.title}>Browse Dropbox</h2>}

          </div>
          <div className={styles.searchContainer}>
            <SearchIcon className={styles.searchIcon} />
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search folders and files..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                className={styles.searchClearButton}
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
              >
                <ClearIcon style={{ fontSize: 18 }} />
              </button>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {onDisconnect && (
              <button
                className={styles.disconnectButton}
                onClick={onDisconnect}
                title="Switch Account"
              >
                Switch Account
              </button>
            )}
            <button className={styles.closeButton} onClick={onClose}>
              <CloseIcon />
            </button>
          </div>
        </div>

        {/* Navigation */}
        {pathHistory.length > 1 && (
          <div className={styles.navigation}>
            <button className={styles.backButton} onClick={handleBackClick}>
              <ArrowBackIcon style={{ marginRight: 8 }} />
              Back
            </button>
          </div>
        )}

        {/* Content */}
        <div className={styles.content}>
          {isLoading && (
            <div className={styles.loadingState}>
              <div className={styles.spinner} />
              <p>Loading...</p>
            </div>
          )}

          {error && (
            <div className={styles.errorState}>
              <p>{error}</p>
              <button
                className={styles.retryButton}
                onClick={() => loadFolder(currentPath)}
              >
                Retry
              </button>
            </div>
          )}

          {!isLoading && !error && items.length === 0 && (
            <div className={styles.emptyState}>
              <FolderIcon style={{ fontSize: 64, color: '#ccc' }} />
              <p>This folder is empty</p>
            </div>
          )}

          {!isLoading && !error && items.length > 0 && filteredItems.length === 0 && (
            <div className={styles.emptyState}>
              <SearchIcon style={{ fontSize: 64, color: '#ccc' }} />
              <p>
                No results found for &ldquo;
                {searchQuery}
                &rdquo;
              </p>
            </div>
          )}

          {!isLoading && !error && filteredItems.length > 0 && (
            <div className={styles.itemList}>
              {filteredItems.map((item) => {
                const isFolder = item['.tag'] === 'folder';
                const isFile = item['.tag'] === 'file';
                const isSelected = isFile ? isFileSelected(item.id) : isFolderSelected(item.id);

                return (
                  <div
                    key={item.id}
                    className={`${styles.item} ${isFolder ? styles.itemFolder : ''} ${isSelected ? styles.itemSelected : ''}`}
                    onClick={() => {
                      if (isFolder) {
                        handleFolderClick(item.path_display);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && isFolder) {
                        handleFolderClick(item.path_display);
                      }
                    }}
                  >
                    <div className={styles.itemIcon}>
                      {getFileIcon(item.name, isFolder)}
                    </div>

                    <div className={styles.itemInfo}>
                      <div className={styles.itemName}>{item.name}</div>
                      {isFile && item.size && (
                        <div className={styles.itemSize}>{formatFileSize(item.size)}</div>
                      )}
                    </div>

                    {isFile && (
                      <button
                        className={styles.selectButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFileToggle(item);
                        }}
                      >
                        {isSelected ? (
                          <CheckBoxIcon style={{ color: '#667eea' }} />
                        ) : (
                          <CheckBoxOutlineBlankIcon />
                        )}
                      </button>
                    )}

                    {isFolder && (
                      <button
                        className={styles.selectButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFolderToggle(item);
                        }}
                        disabled={loadingFolderId === item.id}
                      >
                        {loadingFolderId === item.id ? (
                          <CircularProgress size={20} style={{ color: '#667eea' }} />
                        ) : isSelected ? (
                          <CheckBoxIcon style={{ color: '#667eea' }} />
                        ) : (
                          <CheckBoxOutlineBlankIcon />
                        )}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <div className={styles.footerInfo}>
            {selectedFiles.length > 0 && (
              <span className={styles.selectedCount}>
                {selectedFiles.length}
                {' '}
                file
                {selectedFiles.length !== 1 ? 's' : ''}
                {' '}
                selected
              </span>
            )}
          </div>
          <div className={styles.footerActions}>
            <button className={styles.cancelButton} onClick={onClose}>
              Cancel
            </button>
            {selectedFiles.length < items.filter((item) => item['.tag'] === 'file').length && (
              <button className={styles.cancelButton} onClick={handleSelectAll}>
                Select All
              </button>
            )}
            {selectedFiles.length > 0 && (
              <button className={styles.cancelButton} onClick={handleClearSelection}>
                Clear Selection
              </button>
            )}
            <button
              className={styles.importButton}
              onClick={handleImport}
              disabled={selectedFiles.length === 0}
            >
              Import
              {' '}
              {selectedFiles.length > 0 ? `(${selectedFiles.length})` : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
